import { useCallback } from "react";
import { Controller, type Path, useFormContext } from "react-hook-form";
import { getInitialSelectedOption } from "../../Dropdown/Dropdown.js";
import {
  Dropdown as BaseDropdown,
  type DropdownOption,
  type DropdownProps as BaseDropdownProps,
} from "../../Dropdown/main.js";

export { type DropdownOption } from "../../Dropdown/main.js";

export type DropdownProps<T> = Omit<BaseDropdownProps<T>, "onChange"> & {
  name: string;
};

/**
 * Dropdowns let users select from a list of options.
 *
 * <!-- TODO(ndhoule): Add a screenshot here -->
 *
 * @param props
 * @see {@link Form}
 * @internal
 * @example
 * ```ts
 * import { Dropdown, Form } from "@netlify/sdk/react/components";
 *
 * <Form>
 *   <Dropdown
 *     name="favoriteDog"
 *     options=[
 *       { label: "Pomeranian", value: "pomeranian" },
 *       { label: "Golden Retriever", value: "golden-retriever" },
 *       { label: "Doge", value: "shiba-inu" },
 *     ]
 *   />
 * </Form>
 * ```
 */
export const Dropdown = <
  T,
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  FieldValues extends Record<string, unknown> = Record<string, unknown>,
>({
  name,
  options = [],
  initialSelectedOption,
  ...otherProps
}: DropdownProps<T>) => {
  const { control } = useFormContext<FieldValues>();

  const defaultOption = getInitialSelectedOption(
    [{ label: <>&nbsp;</>, value: undefined }, ...options],
    initialSelectedOption,
  );

  return (
    <Controller
      name={name as unknown as Path<FieldValues>}
      control={control}
      // @ts-expect-error FIXME(ndhoule): It's possible to fix this type error, it's just more work
      // than it's worth right now
      defaultValue={defaultOption?.value}
      render={({ field: { onChange: originalOnChange, value, ...field } }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const onChange = useCallback(
          (value: DropdownOption<T> | null) => {
            originalOnChange(value?.value);
          },
          [originalOnChange],
        );

        return (
          <BaseDropdown
            onChange={onChange}
            {...field}
            initialSelectedOption={initialSelectedOption}
            // Switch the component into controlled mode, allowing react-hook-form to be the source
            // of truth. This makes functionality like the form context's reset() method work.
            //
            // @ts-expect-error FIXME(ndhoule): It's possible to fix this type error, it's just more
            // work than it's worth right now
            selectedItem={
              options.find((option) => option.value === value) ?? defaultOption
            }
            options={options}
            {...otherProps}
          />
        );
      }}
    />
  );
};
